import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Booking, BookingState, UpdateBooking } from '@thro-types/leihbar';
import { ListResponse } from '@thro-types/users';

import { AxiosRequestConfig } from 'axios';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import {
  cancelBooking,
  getBookings,
  getConflictingBookingsInVacation,
  getConflictionBookingsWithOtherBookings,
  patchBooking,
  setBookingsState
} from 'api/leihbar';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

export const useConflictingVacationBookingsQuery = (
  enabled = false,
  config?: AxiosRequestConfig
) => {
  const query = useQuery({
    queryKey: ['bookings', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const bookings = getConflictingBookingsInVacation(config);
      return bookings;
    },
    enabled: enabled,
    initialData: { bookings: [], count: 0 }
  });

  return query;
};

export const useBookingsCheckConflictsQuery = (enabled = false, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['bookings', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const bookings = getConflictionBookingsWithOtherBookings(config);
      return bookings;
    },
    enabled: enabled,
    initialData: { bookings: [], count: 0 }
  });

  return query;
};

export const useBookingsQuery = (config?: AxiosRequestConfig) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const query = useQuery<ListResponse<Booking>, Error>({
    queryKey: ['bookings', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      return getBookings(config);
    },
    placeholderData: keepPreviousData
  });

  useEffect(() => {
    if (query.error) {
      dispatch(
        AlertActions.error(`${t(translations.pages.leihbar.product.alerts.error.getProducts())}`)
      );
      navigate('/', { replace: true });
    }
  }, [dispatch, query.error, t, navigate]);

  return query;
};

export const useBookingMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['bookings'];

  const setState = useMutation<
    Booking[],
    Error,
    { state: BookingState; orderId: number; bookingIds: number[] }
  >({
    mutationFn: async args => {
      return await setBookingsState(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: data => {}
  });

  const update = useMutation<Booking, Error, { id: number; data: UpdateBooking }>({
    mutationFn: async args => {
      return await patchBooking(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: data => {}
  });

  const cancel = useMutation<Booking, Error, { id: number }>({
    mutationFn: async args => {
      return await cancelBooking(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { setState, update, cancel } as const;
};
